import React, {useCallback, useMemo, useState} from 'react';

import {useLocalize} from '../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {
  defineMessage,
  Trans,
} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {
  DropdownSeparator,
  dropdownSeparatorHeight,
} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownSeparator';
import {dropdownListDefaultRowHeight} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownVirtualList';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {SearchDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdown';
import {searchDropdownHeaderHeight} from '../../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdownHeader';
import {isIconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {SyncDropdownInputCustomIconProps} from '../../searchBar/queryBuilder/inboxesSearchQueryBuilderTypes';

const SEPARATOR_HEIGHT = dropdownSeparatorHeight;
const ITEM_HEIGHT = dropdownListDefaultRowHeight;

/*
 * Props.
 */

interface SearchModifierStatusDropdownProps {
  popoverProps: PopoverTransferProps;
  selectedItems: ReadonlyArray<string>;
  items: ReadonlyArray<SyncDropdownInputCustomIconProps>;
  onClick: (arg: string) => void;
  onClear: () => void;
  shouldRenderSeparator?: (index: number) => boolean;
}

/*
 * Component.
 */

export const SearchModifierStatusDropdown = (props: SearchModifierStatusDropdownProps) => {
  const {items, selectedItems, onClick, onClear, popoverProps, shouldRenderSeparator} = props;
  const [query, setQuery] = useState<string>('');

  const isStatusSelected = useCallback(
    (status: string) => selectedItems.some((e) => e === status),
    [selectedItems],
  );

  const localize = useLocalize();
  const headerTitle = localize(
    defineMessage({
      id: `search-hint-is-dropdown-header-title`,
      message: 'Status',
    }),
  );

  const filteredItems = useMemo(
    () => items.filter((e) => e.textLabel.toLowerCase().includes(query.toLowerCase())),
    [items, query],
  );

  const renderItemWithSeparator = (item: SyncDropdownInputCustomIconProps, index: number) => (
    <React.Fragment key={item.inputValue.value}>
      {shouldRenderSeparator && shouldRenderSeparator(index) && <DropdownSeparator />}
      <DropdownItem
        onSelect={() => {
          onClick(item.inputValue.value);
        }}
      >
        {isIconName(item.icon) ? (
          <DropdownItemIcon iconName={item.icon} iconSize={16} label={item.label} />
        ) : (
          <DropdownItemIcon iconSize={16} label={item.label}>
            {item.icon}
          </DropdownItemIcon>
        )}
        <DropdownItemContent>{item.label}</DropdownItemContent>
        <DropdownItemCheckbox isChecked={isStatusSelected(item.inputValue.value)} />
      </DropdownItem>
    </React.Fragment>
  );

  const findRowHeight = useCallback(
    (item: SyncDropdownInputCustomIconProps, index: number) =>
      shouldRenderSeparator && shouldRenderSeparator(index) ? ITEM_HEIGHT + SEPARATOR_HEIGHT : ITEM_HEIGHT,
    [shouldRenderSeparator],
  );

  return (
    <SearchDropdown
      {...popoverProps}
      data={filteredItems}
      query={query}
      maxContentHeight={items.length * dropdownListDefaultRowHeight + searchDropdownHeaderHeight}
      onQueryChange={setQuery}
      headerTitle={headerTitle}
      headerRightIcon={
        selectedItems.length > 0 ? (
          <Button
            variant={ButtonVariantsEnum.LINK}
            size={VisualSizesEnum.SMALL}
            onClick={() => {
              onClear();
              popoverProps.onRequestClose();
            }}
          >
            <Trans id="clear-refinement-button-link">Clear</Trans>
          </Button>
        ) : null
      }
      renderRow={renderItemWithSeparator}
      findRowHeight={findRowHeight}
    />
  );
};
